/**
 * AdminPracticesPage — super_admin-only practice CRUD (Session 10c).
 *
 * Lists every practice with member counts and lets the super_admin create
 * or delete practices. Mutations call refreshPractices() so the header
 * selector and per-page data reload immediately.
 *
 * Backend endpoints (Session 10a):
 *   GET    /api/admin/practices
 *   POST   /api/admin/practices  {name, address, phone_number}
 *   DELETE /api/admin/practices/{id}
 */
import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import axios from '../utils/axiosInstance';
import { usePractice } from '../context/PracticeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const AdminPracticesPage = () => {
  const { refreshPractices } = usePractice();

  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', phone_number: '' });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  // Synchronous in-flight guard. setSubmitting(true) only updates state on the
  // next render, so two near-simultaneous submits (React StrictMode dev mount,
  // accidental double-click, automated test) can both pass the disabled check
  // and fire duplicate POSTs. A ref is mutated synchronously and blocks the
  // second one immediately.
  const createInFlightRef = useRef(false);

  // Practice pending deletion (opens the confirm modal), plus in-flight guard.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState('');   // '' | 'delete'
  // Standalone export (download without deleting): id of the practice exporting.
  const [exportingId, setExportingId] = useState('');
  // Practice id just copied (shows "Kopiert" briefly) — the id the team pastes
  // into the tracker to link a client for the support chat.
  const [copiedId, setCopiedId] = useState('');
  const copiedTimerRef = useRef(null);

  const reload = async () => {
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/practices`);
      setPractices(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('[AdminPractices] list failed:', e);
      setError(e?.response?.data?.detail || 'Praxen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    // Sync guard FIRST so a second submit in the same tick can't slip through.
    if (createInFlightRef.current) return;
    if (!form.name.trim()) {
      setError('Name ist erforderlich.');
      return;
    }
    createInFlightRef.current = true;
    setSubmitting(true);
    setError('');
    setFeedback('');
    try {
      await axios.post(`${API_BASE_URL}/admin/practices`, {
        name: form.name.trim(),
        address: form.address.trim(),
        phone_number: form.phone_number.trim(),
      });
      setFeedback('Praxis angelegt.');
      setForm({ name: '', address: '', phone_number: '' });
      setCreateOpen(false);
      await reload();
      // Refresh header selector so the new practice is selectable immediately.
      refreshPractices();
    } catch (e) {
      console.error('[AdminPractices] create failed:', e);
      setError(e?.response?.data?.detail || 'Praxis konnte nicht angelegt werden.');
    } finally {
      setSubmitting(false);
      createInFlightRef.current = false;
    }
  };

  // Same clipboard idiom as the Tasks page (handleCopy there).
  const handleCopyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopiedId(''), 1500);
    } catch {
      // Clipboard API unavailable (older browsers / non-secure context)
    }
  };

  // Download this practice's COMPLETE data as a .zip (super_admin endpoint, blob):
  // multi-sheet workbook (tasks, transcripts, appointments, voicemails) + the full
  // recordings + voicemail audio — the AVV §9 return-of-all-data hand-off.
  // The endpoint is hard-scoped to p.id server-side, so no cross-practice data.
  const exportTasks = async (p) => {
    const res = await axios.get(
      `${API_BASE_URL}/admin/practices/${p.id}/export`,
      { responseType: 'blob' },
    );
    const cd = res.headers['content-disposition'] || '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const filename = (m && m[1]) || `${(p.name || 'Praxis').replace(/\s+/g, '_')}_Datenexport.zip`;
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // Standalone "Daten exportieren" — download the ZIP without deleting the practice.
  const handleExport = async (p) => {
    if (exportingId) return;
    setExportingId(p.id);
    setError('');
    setFeedback('');
    try {
      await exportTasks(p);
      setFeedback(`Daten für "${p.name}" exportiert.`);
    } catch (e) {
      console.error('[AdminPractices] export failed:', e);
      setError(e?.response?.data?.detail || 'Export fehlgeschlagen.');
    } finally {
      setExportingId('');
    }
  };

  // Confirm modal: just delete. Exporting is a separate, standalone button now,
  // so the modal is a plain "are you sure?" confirmation.
  const confirmDelete = async () => {
    const p = deleteTarget;
    if (!p || deleteBusy) return;
    setError('');
    setFeedback('');
    setDeleteBusy('delete');
    try {
      await axios.delete(`${API_BASE_URL}/admin/practices/${p.id}`);
      setFeedback(`Praxis "${p.name}" gelöscht.`);
      setDeleteTarget(null);
      await reload();
      refreshPractices();
    } catch (e) {
      console.error('[AdminPractices] delete failed:', e);
      setError(e?.response?.data?.detail || 'Praxis konnte nicht gelöscht werden.');
    } finally {
      setDeleteBusy('');
    }
  };

  return (
    <Layout>
      {/* paddingTop pushes content below the absolute-positioned PracticeLogo
          (top 32px + height 75px ≈ 107px), so the right-aligned "Neue Praxis"
          button and the practices table don't sit behind the logo. */}
      <div style={{ padding: '110px 0 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 className="h1" style={{ margin: 0 }}>Praxisverwaltung</h1>
          <button
            type="button"
            className="save-btn"
            onClick={() => { setCreateOpen((v) => !v); setError(''); setFeedback(''); }}
          >
            {createOpen ? 'Abbrechen' : 'Neue Praxis'}
          </button>
        </div>

        {feedback && (
          <div className="save-status success" style={{ marginBottom: 12 }}>{feedback}</div>
        )}
        {error && (
          <div className="save-status error" style={{ marginBottom: 12 }}>{error}</div>
        )}

        {createOpen && (
          <form
            onSubmit={handleCreate}
            style={{
              background: 'var(--surface-card, #fff)',
              border: '1px solid var(--surface-border, #e0e0e0)',
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: '1 / -1' }}>
              <span>Name *</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span>Adresse</span>
              <input
                type="text"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span>Telefonnummer</span>
              <input
                type="text"
                value={form.phone_number}
                onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
              />
            </label>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="submit" className="save-btn" disabled={submitting}>
                {submitting ? 'Erstelle…' : 'Praxis anlegen'}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div>Laden…</div>
        ) : practices.length === 0 ? (
          <div style={{ color: '#666' }}>Keine Praxen vorhanden.</div>
        ) : (
          <div style={{ background: 'var(--surface-card, #fff)', border: '1px solid var(--surface-border, #e0e0e0)', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px' }}>Name</th>
                  <th style={{ padding: '10px 12px' }}>Adresse</th>
                  <th style={{ padding: '10px 12px' }}>Telefon</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Mitglieder</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {practices.map((p) => (
                  <tr key={p.id} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                      {p.name}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, fontSize: '0.78rem', fontWeight: 400, color: '#94a3b8' }}>
                        <span style={{ fontFamily: 'monospace' }}>{p.id}</span>
                        {copiedId === p.id ? (
                          <span style={{ color: 'var(--color-quaternary)', fontWeight: 600 }}>Kopiert</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleCopyId(p.id)}
                            title="Praxis-ID kopieren"
                            aria-label={`Praxis-ID von ${p.name} kopieren`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', padding: 2, lineHeight: 0,
                              background: 'transparent', border: 'none', borderRadius: 3,
                              color: '#888', cursor: 'pointer',
                            }}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#666' }}>{p.address || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#666' }}>{p.phone_number || '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{p.member_count}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => handleExport(p)}
                        disabled={exportingId === p.id}
                        style={{
                          padding: '4px 10px',
                          marginRight: 8,
                          background: '#fff',
                          border: '1px solid #2c7',
                          color: '#1a8',
                          borderRadius: 4,
                          cursor: exportingId === p.id ? 'default' : 'pointer',
                        }}
                      >
                        {exportingId === p.id ? 'Exportiere…' : 'Daten exportieren'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setError(''); setFeedback(''); setDeleteTarget(p); }}
                        style={{
                          padding: '4px 10px',
                          background: '#fff',
                          border: '1px solid #d33',
                          color: '#d33',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {deleteTarget && (
          <div
            role="dialog"
            aria-modal="true"
            onClick={() => { if (!deleteBusy) setDeleteTarget(null); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 8, padding: 24, width: 'min(480px, 92vw)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              }}
            >
              <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>
                Praxis „{deleteTarget.name}“ löschen?
              </h2>
              <p style={{ margin: '0 0 20px', color: '#555', lineHeight: 1.5 }}>
                Diese Praxis und alle zugehörigen Daten (Aufgaben, Gesprächsverläufe, Termine,
                Aufnahmen) sowie {deleteTarget.member_count} Mitgliedschaft(en) werden dauerhaft
                gelöscht. Haben Sie die Daten bereits exportiert?
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  disabled={!!deleteBusy}
                  style={{
                    padding: '8px 14px', background: '#fff', border: '1px solid #ccc',
                    color: '#333', borderRadius: 4, cursor: deleteBusy ? 'default' : 'pointer',
                  }}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={() => confirmDelete()}
                  disabled={!!deleteBusy}
                  style={{
                    padding: '8px 14px', background: '#d33', border: '1px solid #d33',
                    color: '#fff', borderRadius: 4, cursor: deleteBusy ? 'default' : 'pointer',
                  }}
                >
                  {deleteBusy === 'delete' ? 'Lösche…' : 'Löschen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPracticesPage;
